/**
 * ErrorBoundary — Componente global de captura de erros React
 * Fase 2: Robustez e Qualidade
 */
import { Component, type ReactNode, type ErrorInfo } from "react";
import type { ThemeObject } from "../shared/theme.js";

interface ErrorBoundaryProps {
  children: ReactNode;
  T?: ThemeObject;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Log para console (Sentry/LogRocket pode ser integrado aqui)
    console.error("[ErrorBoundary]", error, errorInfo);
    // Se window.__SENTRY__ estiver disponível, reporta automaticamente
    if (typeof window !== "undefined" && (window as unknown as Record<string, unknown>).Sentry) {
      (
        (window as unknown as Record<string, unknown>).Sentry as {
          captureException: (err: Error, opts: Record<string, unknown>) => void;
        }
      ).captureException(error, {
        extra: { componentStack: errorInfo?.componentStack },
      });
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  handleCopyError = () => {
    const errorText = `Error: ${this.state.error?.toString()}\nComponent Stack: ${this.state.errorInfo?.componentStack || ""}`;
    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const T = this.props.T || {
      bg: "#FFFFFF",
      card: "#FFFFFF",
      text: "#1E293B",
      sub: "#64748B",
      border: "#E2E8F0",
      accent: "#2563EB",
    };

    const isDark = T.bg === "#0F172A" || T.bg === "#1E293B";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: T.bg,
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: T.card || T.bg,
            borderRadius: 16,
            border: `1px solid ${T.border}`,
            padding: "48px 40px",
            textAlign: "center",
            boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(220,38,38,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              fontSize: 32,
            }}
          >
            ⚠️
          </div>

          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: T.text,
              margin: "0 0 8px",
            }}
          >
            Algo deu errado
          </h1>

          <p
            style={{
              fontSize: 14,
              color: T.sub,
              margin: "0 0 24px",
              lineHeight: 1.6,
            }}
          >
            Ocorreu um erro inesperado na aplicação. Tente recarregar a página ou voltar ao início.
          </p>

          {/* Error details (collapsible) */}
          {this.state.error && (
            <details
              style={{
                textAlign: "left",
                marginBottom: 24,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: 8,
                padding: "12px 16px",
                border: `1px solid ${T.border}`,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.sub,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 8,
                }}
              >
                Detalhes do erro
              </summary>
              <pre
                style={{
                  fontSize: 11,
                  color: "#DC2626",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: "8px 0 0",
                  fontFamily: "'SF Mono', 'Fira Code', monospace",
                  lineHeight: 1.5,
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack &&
                  "\n\nComponent Stack:" + this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                background: "#2563EB",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.opacity = "0.85")}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.opacity = "1")}
            >
              Recarregar Página
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                border: `1px solid ${T.border}`,
                cursor: "pointer",
                background: "transparent",
                color: T.text,
                fontWeight: 700,
                fontSize: 13,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.opacity = "0.7")}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.opacity = "1")}
            >
              Voltar ao Início
            </button>
            {this.state.error && (
              <button
                onClick={this.handleCopyError}
                style={{
                  padding: "10px 24px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: "transparent",
                  color: this.state.copied ? "#10B981" : "#64748B",
                  fontWeight: 700,
                  fontSize: 13,
                  transition: "opacity 0.2s, color 0.2s",
                }}
                onMouseEnter={(e) => ((e.target as HTMLElement).style.opacity = "0.7")}
                onMouseLeave={(e) => ((e.target as HTMLElement).style.opacity = "1")}
              >
                {this.state.copied ? "Copiado!" : "Copiar Erro"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
